import { useState } from 'react';
import { ErrorMessage } from '@/components/ui/error-message';
import { Spinner } from '@/components/ui/spinner';
import { WorkspaceButton } from '@/components/ui/workspace/button';
import { WorkspaceInput } from '@/components/ui/workspace/field';
import { useHealthDomainsQuery } from '@/lib/api/health-domains';
import {
  useAddDomainMutation,
  useRestoreDomainMutation,
  useRetireDomainMutation,
  type HrDomain,
} from '@/lib/api/health-readiness';
import { POSTURE_LABEL, formatDay } from './model';

/**
 * Manage domains (T-0222 / E20-F01): the lead adds a domain for this
 * engagement, retires one with a reason kept in the audit trail, or
 * restores a retired one. Nothing is deleted; the posture counts the
 * active set. Mirrors `manageDomains()` in the prototype; the retire
 * reason is asked for in place rather than with a browser prompt.
 *
 * Adds and retires go through the existing domain endpoints (POST
 * …/health/domains, PATCH …/health/domains/:id); the retired list is the
 * same endpoint's `is_active=false` read.
 */
export interface ManageDomainsProps {
  engagementId: string;
  domains: HrDomain[];
  onDone: () => void;
}

export function ManageDomains({ engagementId, domains, onDone }: ManageDomainsProps) {
  const retired = useHealthDomainsQuery(engagementId, { is_active: false });
  const add = useAddDomainMutation(engagementId);
  const retire = useRetireDomainMutation(engagementId);
  const restore = useRestoreDomainMutation(engagementId);
  const [newName, setNewName] = useState('');
  const [retiring, setRetiring] = useState<{ id: string; reason: string } | null>(null);

  const retiredRows = retired.data?.domains ?? [];
  const retiringDomain = retiring ? domains.find((d) => d.id === retiring.id) : null;

  return (
    <div className="flex flex-col gap-4" data-testid="manage-domains">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="t-h1">Manage domains</h1>
          <div className="mt-0.5 text-meta text-ink-500">
            This engagement&apos;s domains started from the firm defaults. Retiring keeps the
            history; nothing is deleted.
          </div>
        </div>
        <WorkspaceButton variant="tertiary" onClick={onDone}>
          Done
        </WorkspaceButton>
      </div>

      <section
        aria-label="Active domains"
        className="flex flex-col gap-3 rounded-card border border-ink-100 bg-paper-0 px-4 py-3"
      >
        <h2 className="t-h3">Active ({domains.length})</h2>
        <ul className="flex flex-col gap-1.5">
          {domains.map((d) => (
            <li key={d.id} className="flex flex-col gap-2">
              <div className="grid grid-cols-[110px_1fr_auto] items-center gap-2.5 rounded-card border border-ink-100 bg-paper-0 px-2.5 py-2 text-body-sm text-ink-700">
                <span className="text-[11px] font-medium uppercase tracking-[0.04em] text-ink-500">
                  {POSTURE_LABEL[d.status]}
                </span>
                <span className="truncate text-ink-900">{d.name}</span>
                <WorkspaceButton
                  variant="tertiary"
                  onClick={() => {
                    retire.reset();
                    setRetiring({ id: d.id, reason: '' });
                  }}
                  disabled={retire.isPending}
                  aria-label={`Retire ${d.name}`}
                >
                  Retire
                </WorkspaceButton>
              </div>
              {retiring && retiringDomain && retiring.id === d.id ? (
                <form
                  aria-label={`Retire ${d.name}`}
                  className="flex flex-col gap-2 rounded-card border border-ink-200 bg-paper-50 p-3"
                  onSubmit={(e) => {
                    e.preventDefault();
                    const reason = retiring.reason.trim();
                    if (!reason) return;
                    retire.mutate(
                      { domainId: d.id, reason },
                      { onSuccess: () => setRetiring(null) },
                    );
                  }}
                >
                  <label className="text-body-sm text-ink-700">
                    Retire &ldquo;{d.name}&rdquo; for this engagement? Say why (kept in the audit
                    trail).
                    <textarea
                      value={retiring.reason}
                      onChange={(e) => setRetiring({ id: d.id, reason: e.target.value })}
                      rows={2}
                      className="mt-1.5 w-full rounded-card border border-ink-200 bg-paper-0 px-2.5 py-2 text-body-sm text-ink-900 placeholder:text-ink-300"
                      placeholder="Why this domain no longer applies here"
                    />
                  </label>
                  {retire.error ? <ErrorMessage error={retire.error} /> : null}
                  <div className="flex gap-2">
                    <WorkspaceButton
                      type="submit"
                      variant="destructive"
                      disabled={retire.isPending || retiring.reason.trim().length === 0}
                    >
                      {retire.isPending ? 'Retiring…' : 'Retire domain'}
                    </WorkspaceButton>
                    <WorkspaceButton
                      variant="tertiary"
                      onClick={() => setRetiring(null)}
                      disabled={retire.isPending}
                    >
                      Cancel
                    </WorkspaceButton>
                  </div>
                </form>
              ) : null}
            </li>
          ))}
        </ul>
        <form
          aria-label="Add a domain"
          className="flex flex-wrap items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            const name = newName.trim();
            if (!name) return;
            add.mutate({ name }, { onSuccess: () => setNewName('') });
          }}
        >
          <WorkspaceInput
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Add a domain for this engagement, e.g. Payroll parallel run"
            aria-label="New domain name"
            className="h-[30px] max-w-md flex-1 text-body-sm"
          />
          <WorkspaceButton
            type="submit"
            variant="secondary"
            disabled={add.isPending || newName.trim().length === 0}
          >
            {add.isPending ? 'Adding…' : 'Add'}
          </WorkspaceButton>
        </form>
        {add.error ? <ErrorMessage error={add.error} /> : null}
      </section>

      {retired.isLoading ? (
        <div className="flex items-center gap-2 text-meta text-ink-500">
          <Spinner /> Loading retired domains…
        </div>
      ) : retired.error ? (
        <ErrorMessage error={retired.error} />
      ) : retiredRows.length > 0 ? (
        <section
          aria-label="Retired domains"
          className="flex flex-col gap-3 rounded-card border border-ink-100 bg-paper-0 px-4 py-3"
        >
          <h2 className="t-h3">Retired ({retiredRows.length})</h2>
          {restore.error ? <ErrorMessage error={restore.error} /> : null}
          <ul className="flex flex-col gap-1.5">
            {retiredRows.map((d) => (
              <li
                key={d.id}
                className="grid grid-cols-[110px_1fr_auto] items-center gap-2.5 rounded-card border border-ink-100 bg-paper-0 px-2.5 py-2 text-body-sm text-ink-700"
              >
                <span className="text-[11px] font-medium uppercase tracking-[0.04em] text-ink-500">
                  retired
                </span>
                <span className="min-w-0">
                  <span className="block truncate text-ink-900">{d.name}</span>
                  <span className="block text-meta text-ink-500">{formatDay(d.updated_at)}</span>
                </span>
                <WorkspaceButton
                  variant="tertiary"
                  onClick={() => restore.mutate({ domainId: d.id })}
                  disabled={restore.isPending}
                  aria-label={`Restore ${d.name}`}
                >
                  Restore
                </WorkspaceButton>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}
